import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Dialog, DialogContent, DialogActions, Button, Chip } from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  if (url.startsWith('/api') && process.env.REACT_APP_BACKEND_URL) {
    const backendOrigin = process.env.REACT_APP_BACKEND_URL.replace(/\/api\/?$/, '');
    return `${backendOrigin}${url}`;
  }
  return url;
};

const HomeCarousel = ({ news = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Auto rotate carousel every 8 seconds if more than 1 item
  useEffect(() => {
    if (news.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % news.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [news.length]);

  // Check if content is actually clamped/truncated by CSS
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight + 4);
      }
    };
    checkTruncation();
    const timer = setTimeout(checkTruncation, 120);
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [currentIndex, news]);

  if (!news || news.length === 0) {
    return (
      <Box
        sx={{
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          height: '100%',
          minHeight: { xs: 'auto', md: '250px' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2780bb 0%, #3ba2db 100%)',
            color: 'white',
            minHeight: { xs: '80px', md: '88px' },
            height: { xs: '80px', md: '88px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 4,
            textAlign: 'center',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
            Espacio de Reconocimientos
          </Typography>
        </Box>
        <Box sx={{ p: { xs: 2, md: 2.2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#1f7bb6',
              fontSize: '0.95rem',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            ESPACIO DE RECONOCIMIENTOS
          </Typography>
          <Typography sx={{ color: '#4b5563', fontSize: '0.92rem', lineHeight: 1.65 }}>
            Hemos habilitado un espacio con el que queremos animaros a dejarle un mensaje a es@ compañer@ que siempre tiene una sonrisa para ti.
          </Typography>
        </Box>
      </Box>
    );
  }

  const currentItem = news[currentIndex] || news[0];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? news.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % news.length);
  };

  const resolvedImageUrl = currentItem.imageUrl ? resolveImageUrl(currentItem.imageUrl) : null;

  return (
    <Box
      sx={{
        borderRadius: '18px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        height: '100%',
        minHeight: { xs: 'auto', md: '250px' },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Top Banner with Image or Blue Gradient & Watermark */}
      <Box
        onClick={() => setOpenDetailModal(true)}
        sx={{
          color: 'white',
          px: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: '80px', md: '88px' },
          height: { xs: '80px', md: '88px' },
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...(resolvedImageUrl
            ? {
                backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.35) 0%, rgba(15, 23, 42, 0.72) 100%), url("${resolvedImageUrl}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {
                background: 'linear-gradient(135deg, #257eb9 0%, #3ca4dd 100%)',
              }),
        }}
      >
        {/* Aszende Chevron Watermark (only if no image) */}
        {!resolvedImageUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '180px',
              height: '180px',
              opacity: 0.15,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none" stroke="currentColor">
              <path
                d="M 25 75 L 50 25 L 75 75"
                strokeWidth="14"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        )}

        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.45rem' },
            letterSpacing: -0.5,
            position: 'relative',
            zIndex: 1,
            textShadow: '0 2px 10px rgba(0,0,0,0.4)',
            maxWidth: '90%',
          }}
        >
          {currentItem.title}
        </Typography>

        {news.length > 1 && (
          <>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              size="small"
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.3)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                zIndex: 2,
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.3)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                zIndex: 2,
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {/* Content Section: Clamped text with ellipsis, click opens modal */}
      <Box
        onClick={() => setOpenDetailModal(true)}
        sx={{
          p: { xs: 1.5, md: 1.8 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: '#f8fafc' },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {currentItem.category && (
            <Typography
              sx={{
                fontWeight: 800,
                color: '#1f7bb6',
                fontSize: '0.82rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                mb: 0.6,
              }}
            >
              {currentItem.category}
            </Typography>
          )}

          <Typography
            ref={textRef}
            sx={{
              color: '#4b5563',
              fontSize: '0.86rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              display: '-webkit-box',
              WebkitLineClamp: { xs: 4, sm: 6, md: 8 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentItem.content}
          </Typography>

          {isTruncated && (
            <Typography
              sx={{
                color: '#257eb9',
                fontWeight: 600,
                fontSize: '0.8rem',
                mt: 0.6,
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Leer noticia completa →
            </Typography>
          )}
        </Box>

        {/* Carousel Pagination Dots */}
        {news.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              pt: 1,
              mt: 'auto',
              flexShrink: 0,
            }}
          >
            {news.map((item, index) => (
              <Box
                key={item.id || index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
                sx={{
                  width: index === currentIndex ? 22 : 7,
                  height: 7,
                  borderRadius: '4px',
                  bgcolor: index === currentIndex ? '#257eb9' : '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { bgcolor: index === currentIndex ? '#257eb9' : '#94a3b8' },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Modal para ver la noticia completa */}
      <Dialog
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          },
        }}
      >
        {/* Banner de la noticia en el modal */}
        <Box
          sx={{
            color: 'white',
            px: { xs: 3, sm: 4 },
            py: 4,
            textAlign: 'center',
            position: 'relative',
            minHeight: '150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(resolvedImageUrl
              ? {
                  backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.8) 100%), url("${resolvedImageUrl}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : {
                  background: 'linear-gradient(135deg, #257eb9 0%, #3ca4dd 100%)',
                }),
          }}
        >
          <IconButton
            onClick={() => setOpenDetailModal(false)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
            }}
          >
            <Close />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.1rem' },
              textShadow: '0 2px 10px rgba(0,0,0,0.4)',
              maxWidth: '90%',
            }}
          >
            {currentItem.title}
          </Typography>
        </Box>

        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 } }}>
          {currentItem.category && (
            <Chip
              label={currentItem.category}
              size="small"
              sx={{
                bgcolor: '#e0f2fe',
                color: '#0369a1',
                fontWeight: 700,
                fontSize: '0.78rem',
                mb: 2,
                borderRadius: '8px',
              }}
            />
          )}

          <Typography
            sx={{
              color: '#334155',
              fontSize: '0.96rem',
              lineHeight: 1.75,
              whiteSpace: 'pre-line',
            }}
          >
            {currentItem.content}
          </Typography>

          {currentItem.actionUrl && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                href={currentItem.actionUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: '#257eb9',
                  borderRadius: '10px',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { bgcolor: '#1d6594' },
                }}
              >
                {currentItem.actionText || 'Más información'}
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenDetailModal(false)}
            sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


HomeCarousel.propTypes = {
  news: PropTypes.array,
};

export default HomeCarousel;

